import { useCallback, useEffect, useMemo, useState } from "react";
import { NOTE_CLASS } from "../../lib/ui.js";
import { useMyPermissions } from "../../lib/permissions.js";
import { denialOrRawMessage } from "../../lib/denialReason.js";
import { listDirectoryGroups } from "../../lib/identityClient.js";
import PaginatedPicker, { PickerRow } from "./PaginatedPicker.jsx";
import { Action, ProviderTag } from "./EscalationParts.jsx";
import {
    approverGroupKey,
    getEscalationApproverGroups,
    setEscalationApproverGroups,
} from "../../lib/escalationClient.js";

const PAGE_SIZE = 50;

export default function ApproverGroupsPanel() {
    const caps = useMyPermissions();
    const [saved, setSaved] = useState([]);
    const [draft, setDraft] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [saving, setSaving] = useState(false);
    const [adding, setAdding] = useState(false);

    // Errors are held raw and turned into copy at render, so the posture object
    // never becomes an effect dependency.
    useEffect(() => {
        let cancelled = false;
        setLoading(true);
        getEscalationApproverGroups()
            .then((groups) => {
                if (cancelled) return;
                setSaved(groups);
                setDraft(groups);
                setError(null);
            })
            .catch((err) => { if (!cancelled) setError(err); })
            .finally(() => { if (!cancelled) setLoading(false); });
        return () => { cancelled = true; };
    }, []);

    const savedKeys = useMemo(() => saved.map(approverGroupKey).join("|"), [saved]);
    const draftKeys = useMemo(() => draft.map(approverGroupKey).join("|"), [draft]);
    const dirty = savedKeys !== draftKeys;

    const save = useCallback(async () => {
        setSaving(true);
        setError(null);
        try {
            const groups = await setEscalationApproverGroups(draft);
            setSaved(groups);
            setDraft(groups);
            setAdding(false);
        } catch (err) {
            setError(err);
        } finally {
            setSaving(false);
        }
    }, [draft]);

    const add = useCallback((group) => {
        setDraft((current) => (
            current.some((existing) => approverGroupKey(existing) === approverGroupKey(group))
                ? current
                : [...current, group]
        ));
        setAdding(false);
    }, []);

    return (
        <section className="grid gap-[12px]">
            <header className="grid gap-[3px]">
                <h2 className="m-0 text-[15px] leading-[1.06] font-medium tracking-[-0.04em] text-[color:var(--ink)]">
                    Approver groups
                </h2>
                <p className={`${NOTE_CLASS} m-0 text-[11px] leading-[1.45]`}>
                    Members of these groups can approve, deny, and revoke access.
                    Membership changes apply after directory sync.
                </p>
            </header>

            <div aria-busy={loading ? "true" : undefined} className="grid border-y border-[color:var(--line)]">
                {loading ? (
                    <p className={`${NOTE_CLASS} m-0 py-[12px] text-[11px]`}>Loading approver groups...</p>
                ) : draft.length ? draft.map((group) => (
                    <div
                        key={approverGroupKey(group)}
                        className="flex min-w-0 items-center justify-between gap-[10px] border-b border-[color:var(--line-soft)] py-[10px] last:border-b-0"
                    >
                        <span className="grid min-w-0 gap-[2px]">
                            <span className="flex min-w-0 items-center gap-[6px]">
                                <span className="truncate text-[12px] leading-[1.16] font-medium text-[color:var(--ink)]">
                                    {group.displayName || "No longer in the directory"}
                                </span>
                                <ProviderTag provider={group.sourceProvider} />
                            </span>
                            <span className={`${NOTE_CLASS} truncate text-[10px]`} title={group.sourceId}>
                                {group.sourceId}
                            </span>
                        </span>
                        <button
                            type="button"
                            onClick={() => setDraft((current) => current.filter(
                                (existing) => approverGroupKey(existing) !== approverGroupKey(group),
                            ))}
                            className="shrink-0 text-[10px] uppercase tracking-[0.08em] text-[color:var(--muted)] hover:text-red-400 cursor-pointer"
                        >
                            Remove
                        </button>
                    </div>
                )) : (
                    <p className={`${NOTE_CLASS} m-0 py-[12px] text-[11px] leading-[1.45]`}>
                        No approver groups. Only users with approval permission can approve requests.
                    </p>
                )}
            </div>

            {adding ? (
                <GroupPicker onAdd={add} onCancel={() => setAdding(false)} exclude={draft} />
            ) : (
                <div>
                    <Action onClick={() => setAdding(true)}>Add a group</Action>
                </div>
            )}

            {error ? (
                <p className="m-0 border border-red-400/40 bg-red-400/10 px-[10px] py-[8px] text-[11px] leading-[1.4] text-red-400">
                    {denialOrRawMessage(error, caps)}
                </p>
            ) : null}

            {dirty ? (
                <div className="flex items-center justify-end gap-[8px] border-t border-[color:var(--line-soft)] pt-[10px]">
                    <Action onClick={() => { setDraft(saved); setError(null); }}>Discard</Action>
                    <Action tone="primary" disabled={saving} onClick={save}>
                        {saving ? "Saving..." : "Save approvers"}
                    </Action>
                </div>
            ) : null}
        </section>
    );
}

function GroupPicker({ onAdd, onCancel, exclude }) {
    const caps = useMyPermissions();
    const excluded = useMemo(() => new Set(exclude.map(approverGroupKey)), [exclude]);

    const fetchPage = useCallback(async ({ search, cursor }) => {
        const page = await listDirectoryGroups({
            search,
            page_size: PAGE_SIZE,
            page_token: cursor ?? "",
        });
        return {
            rows: (page?.groups ?? []).map((group) => ({
                sourceProvider: group.sourceProvider ?? "",
                integrationId: group.integrationId ?? "",
                sourceId: group.sourceId ?? "",
                displayName: group.displayName || group.sourceId || "",
                memberCount: Number(group.memberCount ?? 0),
            })),
            total: page?.totalCount ?? null,
            nextCursor: page?.nextPageToken || null,
        };
    }, []);

    return (
        <div className="grid gap-[6px] border border-[color:var(--line)] bg-[color:var(--control-bg)] p-[10px]">
            <PaginatedPicker
                autoFocus
                placeholder="Search directory groups"
                ariaLabel="Search directory groups"
                emptyText="No groups match that search."
                errorText={(err) => denialOrRawMessage(err, caps)}
                fetchPage={fetchPage}
                renderRow={(group) => {
                    const key = approverGroupKey(group);
                    const already = excluded.has(key);
                    return (
                        <PickerRow
                            key={key}
                            primary={group.displayName}
                            secondary={already ? "already added" : `${group.memberCount.toLocaleString()} members`}
                            badge={<ProviderTag provider={group.sourceProvider} />}
                            selected={already}
                            onSelect={() => { if (!already) onAdd(group); }}
                        />
                    );
                }}
            />
            <div className="flex justify-end">
                <Action onClick={onCancel}>Cancel</Action>
            </div>
        </div>
    );
}
